import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/PublicNav";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <button type="button" className="btn btn-outline w-full" onClick={go} data-testid="google-auth-btn">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
      Continue with Google
    </button>
  );
}

export default function Signup() {
  const { register, resendVerification } = useAuth();
  const [form, setForm] = useState({ first_name: "", email: "", password: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const strong = form.password.length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accept) { setError("Please confirm you are 18+ and accept the Terms."); return; }
    if (!strong) { setError("Password needs at least 10 characters."); return; }
    setLoading(true);
    const res = await register({ ...form, accept_terms: accept });
    setLoading(false);
    if (res.ok) setSent(true); else setError(res.error);
  };

  const doResend = async () => { const r = await resendVerification(); if (r.ok) setResent(true); };

  if (sent) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="dk-card p-8 max-w-md text-center space-y-4" data-testid="verify-prompt">
          <span className="grid place-items-center w-12 h-12 rounded-full mx-auto" style={{ background: "var(--sage-tint)" }}><Check style={{ color: "var(--sage)" }} /></span>
          <h1 className="font-serif text-[26px] font-semibold">Check your email</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>We've sent a verification link to <b>{form.email}</b>. Click it to start your free 3-day trial — it expires in 24 hours.</p>
          {resent
            ? <p className="text-sm" style={{ color: "var(--success)" }}>Sent again — check your inbox and spam folder.</p>
            : <button className="btn btn-outline w-full" onClick={doResend} data-testid="resend-verify-btn">Resend email</button>}
          <button className="btn btn-ghost w-full" onClick={() => nav("/app/onboarding")} data-testid="continue-setup-btn">Continue to set-up meanwhile →</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 dk-raised border-r">
        <Logo />
        <div>
          <h2 className="font-serif text-[32px] font-semibold">3 days, everything, free.</h2>
          <ul className="mt-4 space-y-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {["All decode tools", "Up to 5 child profiles", "No credit card", "Cancel any time"].map((i) => (
              <li key={i} className="flex items-center gap-2"><Check size={16} style={{ color: "var(--success)" }} /> {i}</li>
            ))}
          </ul>
        </div>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>Your family's details are encrypted &amp; private.</span>
      </div>
      <div className="grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="signup-form">
          <div className="lg:hidden mb-4"><Logo /></div>
          <h1 className="font-serif text-[28px] font-semibold">Create your account</h1>
          {error && <div className="rounded-xl p-3 text-sm" style={{ background: "#F3DEDA", color: "var(--error)" }} data-testid="signup-error">{error}</div>}
          <GoogleButton />
          <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}><span className="flex-1 border-t" /> or <span className="flex-1 border-t" /></div>
          <div>
            <label className="text-sm font-medium">First name</label>
            <input className="dk-input mt-1" value={form.first_name} onChange={set("first_name")} required data-testid="signup-firstname" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input className="dk-input mt-1" type="email" value={form.email} onChange={set("email")} required data-testid="signup-email" />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <input className="dk-input mt-1" type="password" value={form.password} onChange={set("password")} required data-testid="signup-password" />
            <p className="text-xs mt-1" style={{ color: form.password ? (strong ? "var(--success)" : "var(--text-muted)") : "var(--text-muted)" }}>At least 10 characters {strong && "✓"}</p>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1" data-testid="signup-terms" />
            <span style={{ color: "var(--text-muted)" }}>I'm 18 or over and a parent or guardian, and I accept the Terms &amp; Privacy Policy.</span>
          </label>
          <button className="btn btn-primary w-full" disabled={loading} data-testid="signup-submit">{loading && <Loader2 size={16} className="animate-spin" />} Start free trial</button>
          <p className="text-sm text-center" style={{ color: "var(--text-muted)" }}>Already have an account? <Link className="link" to="/auth/login">Log in</Link></p>
        </form>
      </div>
    </div>
  );
}
